import type { TViolationType } from "../../interfaces/statues";
import { type IState, type TNextAvailabilityEventDate, type TNextAvailabilityEventFn } from "../helpers";
import { computeCycleDutyResult, computeCycleDrivingResult, extractCycleDutyConditions, extractCycleDrivingConditions } from "./cycle";
import { computeCycleEndResult, extractCycleEndConditions } from "./cycleEnd";
import { computeCycleRestResult, extractCycleRestConditions } from "./cycleRest";
import { computeDailyDrivingResult, extractDailyDrivingConditions } from "./dailyDriving";
import { computeDailyDutyResult, extractDailyDutyConditions } from "./dailyDuty";
import { computeDailyOffResult, extractDailyOffConditions } from "./dailyOff";
import { computeDailyRestResult, extractDailyRestConditions } from "./dailyRest";
import { computeDrivingResult, extractDrivingConditions } from "./driving";
import { computeRestResult, extractRestConditions } from "./rest";
import { computeWorkdayDutyResult, extractWorkdayDutyConditions } from "./workdayDuty";
import { computeDutySinceCycleRestResult, extractDutySinceCycleRestConditions } from "./dutySinceCycleRest";
import { computeWorkdayResult, extractWorkdayConditions } from "./workday";
import type { TComputedAvailabilityResult } from "./helpers/shared";

/**
 * Next HOS Event Calculators
 *
 * Each function predicts the next inflection point in a driver's HOS availability for one
 * violation type — a future time when availability will change rate or reset to a new value.
 * These dates drive an outer system that re-evaluates driver state at each predicted event.
 *
 * == Core Invariants ==
 *
 * 1. Stability         — The predicted event time is a fixed future timestamp, invariant as time
 *                        advances toward it. A shifting prediction risks sliding past the actual
 *                        inflection point, violating invariant 4.
 * 2. Rate correctness  — Between now and the event, availability changes at exactly the predicted
 *                        rate with no deviations.
 * 3. Rate consistency  — isConsuming is the same at T and T+ε.
 * 4. Bounded prediction — The predicted event must be ≤ the actual earliest inflection point.
 *                        Both directions of error have costs. Returning a date later than the
 *                        actual event is a correctness failure: the outer system misses the
 *                        inflection and serves stale data to customers. Returning a date earlier
 *                        than the actual event is a performance failure: it triggers an
 *                        unnecessary re-evaluation in the outer system. The goal is the exact
 *                        inflection time. When that is not computable from current state, an
 *                        earlier date may be returned as documented technical debt — declared via
 *                        conservativeReason in FACTOR_LIMITATIONS — not as an acceptable design
 *                        choice. See §Codebase Constraints below.
 * 5. Validity          — Every predicted event corresponds to a real inflection: a rate change,
 *                        a value discontinuity (reset/step), or both. Discontinuities are
 *                        first-class inflection events and must be predicted just like rate
 *                        changes — do not avoid returning them because the jump feels
 *                        "discontinuous." A common example: when a driver crosses midnight,
 *                        their daily availability resets discontinuously to the ruleset maximum,
 *                        so midnight is always a predicted event for availability types that
 *                        reset daily. Code-level artifacts such as formula switches in
 *                        availability helpers produce the same kind of discontinuous jump and
 *                        are equally valid reasons to return an event date, even if they will
 *                        be refactored away in the future.
 *                        Note: the rate correctness check (Invariant 2) is verified between the
 *                        evaluation point and justBeforePrediction — immediately before the
 *                        event. A discontinuity that occurs at the event itself does not affect
 *                        the rate check.
 *
 * == Scope: Time-Based Inflections Only ==
 *
 * These calculators predict events driven by the passage of time given the CURRENT log history —
 * a future timestamp T such that, with the same set of DutyStatusLogs evaluated at T, the
 * driver's availability changes. Log-triggered events are outside scope.
 *
 * A log-triggered event is one that only occurs because a new DutyStatusLog is added (e.g., a
 * driver returning to on-duty triggers a workday reset via the ERR exemption). Such events are
 * not predictable from state alone. The outer system re-evaluates whenever a new log is added;
 * calculators do not predict that re-evaluation.
 *
 * Practical consequence: when a state has both a log-triggered inflection (which may fire
 * earlier) and a time-based inflection (which fires later but unconditionally), predict the
 * time-based inflection. It is real, stable, and detectable by the regression checker.
 * Example: WorkdayResetErrPending can also reset when the driver logs on-duty in the [4h, 8h)
 * window, but that requires a new log. The time-based reset at breakStart + errMinimum (4h) fires
 * unconditionally — the eligibleReducedRestPeriod closes time-based at that point, decrementing
 * offDutyMinimum to 4h and immediately satisfying the threshold. That is the correct prediction.
 *
 * The regression checker enforces this implicitly: it trims logs to <= evalTime, so any
 * predicted event must produce a detectable state change without future logs. A factor whose
 * predicted date only corresponds to a state change triggered by a future log will fail the
 * confirmed-factor spuriousness check, which is the correct signal that the wrong date was chosen.
 *
 * == How the Regression Checker Validates These Predictions ==
 *
 * The regression checker answers the question: "when will availability inflect, assuming no
 * new logs ever arrive?" It validates correctness across the full test suite by advancing the
 * evaluation time (toDate) past each log pair, while keeping the log set fixed at the logs
 * available up to evalTime (future logs are ignored). This lets the checker verify the
 * predicted nextAvailabilityEventDate at every date pair in the test data — not just the
 * specific scenarios written in unit tests.
 *
 * Practical consequence for calculator authors: when reasoning about whether a predicted event
 * is correct, always ask "if this driver stays in their current status indefinitely (no new
 * log ever arrives), does the availability actually change at the predicted time?" If the answer
 * is no — because the event requires a future log — then the prediction is wrong and will fail
 * the spuriousness check. If the answer is yes — the hosCalculator changes availability at that
 * time based solely on elapsed time — then the prediction is correct and confirmed (no
 * conservativeReason) is appropriate. This invariant is the direct link between "time-based inflection" and testability.
 *
 * == Codebase Constraints ==
 *
 * Some event factors cannot currently be expressed as a fixed timestamp from current state —
 * for example when computing an event time would require resolving a chain of intermediate
 * state transitions. This is a codebase limitation, not a theoretical one: ideally all factors
 * would be computable.
 *
 * When a factor cannot be computed stably, it may return a date earlier than the actual event
 * as a last resort. This is a codebase limitation, not a design choice — it carries a real
 * performance cost (an unnecessary re-evaluation in the outer system). Any factor using this
 * pattern MUST be registered in FACTOR_LIMITATIONS with a conservativeReason entry documenting
 * what makes the exact time uncomputable and what would be needed to fix it. The regression
 * checker skips the spuriousness check for conservative factors. Every conservativeReason entry
 * in FACTOR_LIMITATIONS is tracked technical debt; the goal is to eliminate all of them.
 *
 * "Earlier" means a genuine future date that precedes the real event — not the current
 * evaluation time. Returning the evaluation time causes the regression checker to skip the
 * point via its "<= to" guard, removing test coverage rather than being conservative. The one
 * legitimate exception is when the event is genuinely occurring at the current instant (e.g. a
 * constraint switch triggered at this exact log boundary) — in that case returning the
 * evaluation time correctly signals an immediate re-evaluation and the checker skip is
 * appropriate. Where a conservative earlier date is returned, an inline comment must document:
 * what event is uncomputable, why, and what information or formula would make it computable.
 *
 * Before using any state property as an event time anchor, verify that its value is determined
 * at log processing time and does not advance as the evaluation time advances. A property that
 * tracks the current evaluation instant rather than a fixed historical fact will produce
 * predictions that shift forward between evaluations, violating stability invariant 1.
 *
 * Omitting event dates entirely is never a valid fallback. A missing nextAvailabilityEventDate
 * and nextExhaustionEventDate together assert that the current rate and value will remain
 * unchanged indefinitely — an extremely strong claim that is almost never true for active HOS
 * states. Each evaluated state must be able to describe its current rate (isConsuming) and
 * predict when that rate will next change. If the exact change time cannot be computed stably,
 * return an earlier date to force re-evaluation; do not suppress the prediction. Omitting event
 * dates to avoid an unstable calculation silently breaks the outer system's ability to detect
 * the inflection and will cause it to serve stale availability data to customers.
 *
 * == Engineering Conventions ==
 *
 * 1. Compute each event factor independently and label it clearly.
 * 2. Return min(all computable event times).
 * 3. When a factor is excluded due to a codebase constraint, document it inline so it can be
 *    located and improved without reverse-engineering the surrounding logic.
 *
 * These invariants are verified by checkForAvailabilityRegressions in tests/helpers/hosEventDateRegressionCheckers.ts.
 *
 * == Future Improvements ==
 *
 * Raw (unclamped) availability values: DONE.
 * All calculator functions pass enforceNonNegative = false to discreteAvailabilityFunctions,
 * receiving raw values that can be negative when the driver is already in violation. Each
 * calculator branch handles the "already in violation" case explicitly — returning undefined
 * from the exhaustion factor when no future zero-crossing exists.
 *
 * Consuming calculators with positive availability: enforced at test level via the spuriousness check.
 * With the AvailabilityCalculator base class, exhaustion() is the designated place for zero-crossing
 * dates (feeding nextExhaustionEventDate). The spuriousness check catches any zero-crossing
 * accidentally placed in availabilityFactors() — it fires at a date that does not correspond to a
 * rate change, so the checker flags it spurious. A consuming calculator that omits the zero-crossing
 * entirely will fail the 60-day rate check. The runtime assertion proposed here is not needed;
 * the structural split enforces the invariant at compile time and the checker validates it at test time.
 * The isConsuming: false with no event date case (frozen at positive availability with no computable
 * structural event approaching) is correct and is already validated empirically by the 60-day stability
 * check — no separate assertion is needed for that case.
 */

/** A function that computes the per-factor breakdown for one availability type. */
export type TFactorComputeFn = (state: IState) => TComputedAvailabilityResult | undefined;

/** A function that extracts the decision-driving conditions from state for one availability type. */
export type TExtractConditionsFn = (state: IState) => Record<string, unknown>;

/**
 * Both functions required for a fully implemented calculator.
 * Exposed via calculatorRegistry so the regression checker can call extractConditions on failure.
 */
export type TCalculatorRecord = {
    computeResult: TFactorComputeFn;
    extractConditions: TExtractConditionsFn;
};

/**
 * A calculator entry: either a fully implemented calculator or an explicit stub.
 *
 * Implemented entries store both computeResult and extractConditions. The event function is
 * derived centrally via makeEventFn, which guarantees that every implemented calculator
 * extracts dates from its compute result in exactly the same way — mismatched pairs are
 * structurally impossible.
 *
 * Stubs are declared explicitly ({ kind: "stub" }) rather than being silent undefined/noop
 * entries, making unimplemented violation types visible at a glance.
 */
type TCalculatorEntry =
    | { kind: "implemented"; computeResult: TFactorComputeFn; extractConditions: TExtractConditionsFn }
    | { kind: "stub" };

/**
 * Derives a TNextAvailabilityEventFn from a compute function by extracting the three
 * output fields that the outer system consumes. All implemented calculators use this
 * factory — the event function is never written by hand in a calculator file.
 */
const makeEventFn = (computeFn: TFactorComputeFn): TNextAvailabilityEventFn => (state) => {
    const r = computeFn(state);
    if (!r) return undefined;
    return {
        isConsuming: r.isConsuming,
        nextAvailabilityEventDate: r.nextAvailabilityEvent?.date,
        nextExhaustionEventDate: r.nextExhaustionEvent?.date,
    };
};

/**
 * Single source of truth for all nextAvailabilityEvent calculators.
 *
 * TypeScript requires every TViolationType to appear here. Adding a new violation type to the
 * union forces a compile error until an entry is added — either "implemented" or "stub".
 *
 * To register a new calculator:
 *   1. Add its source file to src/rules/nextAvailabilityEvents/
 *   2. Import computeResult at the top of this file
 *   3. Change the entry from { kind: "stub" } to { kind: "implemented", computeResult }
 *   Both derived maps below update automatically — no other changes needed here.
 *
 * Known gaps (see §7 of tests/nextAvailabilityEvent/AGENTS.md for details):
 *   - File names are not enforced to match violation types; an orphaned file won't cause a
 *     compile error if it is never imported here.
 */
const calculatorEntries: Record<TViolationType, TCalculatorEntry> = {
    Rest:               { kind: "implemented", computeResult: computeRestResult,           extractConditions: extractRestConditions },
    Driving:            { kind: "implemented", computeResult: computeDrivingResult,         extractConditions: extractDrivingConditions },
    Duty:               { kind: "implemented", computeResult: computeWorkdayDutyResult,     extractConditions: extractWorkdayDutyConditions },
    Cycle:              { kind: "implemented", computeResult: computeCycleDutyResult,       extractConditions: extractCycleDutyConditions },
    CycleDriving:       { kind: "implemented", computeResult: computeCycleDrivingResult,    extractConditions: extractCycleDrivingConditions },
    DailyDriving:       { kind: "implemented", computeResult: computeDailyDrivingResult,    extractConditions: extractDailyDrivingConditions },
    DailyDuty:          { kind: "implemented", computeResult: computeDailyDutyResult,       extractConditions: extractDailyDutyConditions },
    DailyOff:           { kind: "implemented", computeResult: computeDailyOffResult,        extractConditions: extractDailyOffConditions },
    DailyRest:          { kind: "implemented", computeResult: computeDailyRestResult,       extractConditions: extractDailyRestConditions },
    CycleRest:          { kind: "implemented", computeResult: computeCycleRestResult,       extractConditions: extractCycleRestConditions },
    DutySinceCycleRest: { kind: "implemented", computeResult: computeDutySinceCycleRestResult, extractConditions: extractDutySinceCycleRestConditions },
    Workday:            { kind: "implemented", computeResult: computeWorkdayResult, extractConditions: extractWorkdayConditions },
    CycleEnd:           { kind: "implemented", computeResult: computeCycleEndResult, extractConditions: extractCycleEndConditions },
};

const stubEventFn: TNextAvailabilityEventFn = (_state) => undefined;

/** Derived from calculatorEntries. Do not modify directly — update calculatorEntries instead. */
export const discreteAvailabilityWithConsiderationsFunctions: { [violationType in TViolationType]: TNextAvailabilityEventFn } = (() => {
    const result = {} as { [violationType in TViolationType]: TNextAvailabilityEventFn };
    for (const key in calculatorEntries) {
        const vt = key as TViolationType;
        const entry = calculatorEntries[vt];
        result[vt] = entry.kind === "implemented" ? makeEventFn(entry.computeResult) : stubEventFn;
    }
    return result;
})();

/**
 * Derived from calculatorEntries. Do not modify directly — update calculatorEntries instead.
 *
 * Used by checkForFactorOptimalityRegressions to verify that every confirmed factor
 * (no conservativeReason) causes a detectable change in discrete availability when it fires,
 * and to call extractConditions on failure for diagnostic output.
 */
export const calculatorRegistry: Record<TViolationType, TCalculatorRecord | undefined> = (() => {
    const result = {} as Record<TViolationType, TCalculatorRecord | undefined>;
    for (const key in calculatorEntries) {
        const vt = key as TViolationType;
        const entry = calculatorEntries[vt];
        result[vt] = entry.kind === "implemented"
            ? { computeResult: entry.computeResult, extractConditions: entry.extractConditions }
            : undefined;
    }
    return result;
})();

export const getNextAvailabilityEventDates = (state: IState): TNextAvailabilityEventDate => {
    const result: TNextAvailabilityEventDate = {};
    for (const key in discreteAvailabilityWithConsiderationsFunctions) {
        const violationType = key as TViolationType;
        const value = discreteAvailabilityWithConsiderationsFunctions[violationType](state);
        if (value !== undefined) {
            result[violationType] = value;
        }
    }
    return result;
};
